import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_ASSET_OWNER } from "../../apis/settingsApis/assets-apis/allAssetsApis";
import { GET_STAFF_DETAILS } from "../../apis/organization-apis/people-apis/peopleApis";
import ConfirmSnackbar from "../Snackbar/ConfirmSnackbar";

// Sentinel option: Autocomplete needs a selectable value to express "nobody",
// and null already means "cleared the input".
const UNASSIGNED = { id: "unassigned", name: "Unassigned", email: "" };

/**
 * Reassign a device to a different employee.
 *
 * Mirrors the People page's EditManagerDialog — same Autocomplete-in-a-small-
 * dialog shape, opened from a pencil next to the field it edits.
 */
const EditAssetOwnerDialog = ({ open, onClose, asset, onSaved }) => {
  const [updateAssetOwner, { loading: updating }] = useMutation(UPDATE_ASSET_OWNER);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(UNASSIGNED);

  // Same source and shape as AddAssetDialog's owner picker: ownerEmployeeId is
  // employee_data_id, not the employees-table id.
  const { data: staffData, loading: staffLoading } = useQuery(GET_STAFF_DETAILS, {
    skip: !open,
    fetchPolicy: "cache-first",
  });

  const ownerOptions = useMemo(() => {
    const people = (staffData?.getEmployees?.data ?? [])
      .filter((e) => e.in_scope !== false)
      .map((e) => ({
        id: e.employee_data_id,
        name: e.name || `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim(),
        email:
          e.work_email ||
          e.emails?.find((em) => em.type === "work")?.email ||
          e.emails?.find((em) => em.is_primary)?.email ||
          e.emails?.[0]?.email ||
          "",
      }));
    return [UNASSIGNED, ...people];
  }, [staffData]);

  // Seed from the asset each time the dialog opens. The asset row carries
  // ownerEmail/ownerName but not always ownerEmployeeId, so fall back to
  // matching on email before giving up and showing Unassigned.
  useEffect(() => {
    if (!open) return;
    const byId = asset?.ownerEmployeeId
      ? ownerOptions.find((o) => o.id === asset.ownerEmployeeId)
      : null;
    const byEmail = asset?.ownerEmail
      ? ownerOptions.find(
          (o) => o.email && o.email.toLowerCase() === String(asset.ownerEmail).toLowerCase()
        )
      : null;
    setSelectedOwner(byId || byEmail || UNASSIGNED);
  }, [open, asset, ownerOptions]);

  const handleSave = async () => {
    if (!asset?.assetId) return;
    const nextOwnerId = selectedOwner?.id === UNASSIGNED.id ? null : selectedOwner?.id || null;

    try {
      const { data } = await updateAssetOwner({
        variables: { assetId: asset.assetId, ownerEmployeeId: nextOwnerId },
      });
      const res = data?.updateAssetOwner;
      if (res?.success) {
        setSnackbarInfo({
          message: res.message || "Owner updated",
          vertical: "top",
          horizontal: "right",
        });
        await onSaved?.(res.data);
        onClose();
      } else {
        setSnackbarInfo({
          message: res?.message || "Failed to update owner",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to update owner",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}
        >
          <Typography variant="h5" fontWeight={600}>
            Edit Owner
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              options={ownerOptions}
              loading={staffLoading}
              getOptionLabel={(option) => option?.name || option?.email || ""}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              value={selectedOwner}
              onChange={(_event, newValue) => setSelectedOwner(newValue || UNASSIGNED)}
              disabled={updating}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Owner"
                  placeholder="Search for an employee..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {option.name}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={updating}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={updating}>
            {updating ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default EditAssetOwnerDialog;
